import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { useLocation } from "@/hooks/application/location";
import { useAppSelector } from "@/hooks/application/store";
import { PaginatedResult } from "@/interfaces/common/PaginatedResult";
import { Store } from "@/interfaces/domain/Store";
import { StorePaymentMethod } from "@/interfaces/domain/StorePaymentMethod";
import {
  LocationParams,
  getHighlightStoresRequest,
  getStoreByIdRequest,
  getStorePaymentMethodsRequest,
  getStoresByClientIdRequest,
  getStoresByLocationRequest,
  getStoresRequest,
} from "@/services/api/store";
import { logger } from "@/services/logger";

import { useAddress } from "./address";

export const STORE_QUERY_KEY = ({
  clientId,
  location,
}: {
  location?: LocationParams;
  clientId?: string;
}) => {
  if (clientId) return `/stores/by-client-id/${clientId}`;
  if (location) return `/stores?lat=${location.latitude}&long=${location.longitude}`;
  return "/stores";
};

export const useStores = (options?: UseInfiniteQueryOptions<PaginatedResult<Store>>) => {
  const { user } = useAppSelector((store) => store.auth);

  const { principalAddress } = useAddress();
  const clientId = user?.cliente?._id;

  const [isLoadingLocation, setLocadingLocation] = useState(true);

  const { location } = useLocation({
    onSuccess: () => {
      setLocadingLocation(false);
    },
    onError: () => {
      setLocadingLocation(false);
    },
  });

  const getStores = useCallback(
    async (page: number = 1) => {
      if (clientId && principalAddress) {
        return getStoresByClientIdRequest(page, clientId);
      }
      if (location) {
        return getStoresByLocationRequest(page, location);
      }
      return getHighlightStoresRequest();
    },
    [clientId, principalAddress, location]
  );

  const { data, ...rest } = useInfiniteQuery<PaginatedResult<Store>>({
    queryKey: [STORE_QUERY_KEY({ location, clientId })],
    queryFn: ({ pageParam = 1 }) => getStores(pageParam),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    enabled: !principalAddress ? !isLoadingLocation : true,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });

  useEffect(() => {
    if (data?.pages?.length) {
      setLocadingLocation(false);
    }
  }, [data?.pages]);

  return {
    data: data?.pages?.map((page) => page.docs).flat(),
    ...rest,
    isLoading: rest.isLoading || isLoadingLocation,
  };
};

export const useStoresByTypeId = (
  typeId: string,
  options?: UseInfiniteQueryOptions<PaginatedResult<Store>>
) => {
  return useInfiniteQuery<PaginatedResult<Store>>({
    queryKey: [`/stores/by-type-id/${typeId}`],
    queryFn: ({ pageParam = 1 }) => getStoresRequest({ page: pageParam, typeId }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};

export const STORE_BY_ID_QUERY_KEY = (id: string) => `/stores/${id}`;

export const useStoreById = (id: string, options?: UseQueryOptions<Store>) => {
  return useQuery<Store>({
    queryKey: [STORE_BY_ID_QUERY_KEY(id)],
    queryFn: () => getStoreByIdRequest(id),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};

export const useStorePaymentMethods = (
  storeId: string,
  options?: UseQueryOptions<StorePaymentMethod[]>
) => {
  return useQuery<StorePaymentMethod[]>({
    queryKey: [`/store-payment-methods/${storeId}`],
    queryFn: () => getStorePaymentMethodsRequest(storeId),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};
